import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  LinearProgress,
  Typography,
  Box,
  Chip,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { paymentService } from '../services/api';
import { uploadService } from '../services/uploadService';
import dayjs from 'dayjs';

interface PartialPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  student: any;
  month: string;
  year: number;
  totalDue: number;
  paidAmount: number;
  paymentId?: number;
}

const PartialPaymentDialog: React.FC<PartialPaymentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  student,
  month,
  year,
  totalDue,
  paidAmount,
  paymentId
}) => {
  const [amount, setAmount] = useState<number>(0);
  const [paymentDate, setPaymentDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [reference, setReference] = useState('');
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const pendingBalance = totalDue - paidAmount;
  const maxAmount = pendingBalance;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setProofFile(file);
    }
  };

  const handleSubmit = async () => {
    if (amount <= 0) {
      setError('El monto debe ser mayor a 0');
      return;
    }

    if (amount > maxAmount) {
      setError(`El monto no puede exceder el saldo pendiente (${maxAmount} RUB)`);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let proofUrl = null;
      if (proofFile) {
        setUploading(true);
        const uploadResponse = await uploadService.uploadPaymentProof(proofFile);
        proofUrl = uploadResponse.data.file_url;
        setUploading(false);
      }

      const paymentData = {
        student_id: student.id,
        parent_id: student.parent_id,
        month,
        year,
        amount,
        payment_date: paymentDate,
        bank_reference: reference,
        comments,
        payment_proof_url: proofUrl
      };

      if (paymentId) {
        // Actualizar pago existente
        await paymentService.updateMonthly(paymentId, {
          ...paymentData,
          additional_payment: true
        });
      } else {
        // Crear nuevo pago
        await paymentService.createMonthly(paymentData);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al registrar el pago');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Registro de Pago Parcial
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Información del estudiante */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Estudiante
          </Typography>
          <Typography variant="h6">
            {student.first_name} {student.last_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {month} {year} - Grado: {student.grade}
          </Typography>
        </Box>

        {/* Progreso de pago */}
        <Box sx={{ mb: 3 }}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2">
              Progreso de pago
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {((paidAmount / totalDue) * 100).toFixed(1)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={(paidAmount / totalDue) * 100}
            sx={{ height: 10, borderRadius: 5 }}
          />
          <Box display="flex" justifyContent="space-between" mt={1}>
            <Typography variant="caption" color="text.secondary">
              Pagado: {paidAmount} RUB
            </Typography>
            <Typography variant="caption" color="error.main">
              Pendiente: {pendingBalance} RUB
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total: {totalDue} RUB
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Formulario de pago parcial */}
        <TextField
          fullWidth
          type="number"
          label="Monto a pagar"
          value={amount}
          onChange={(e) => setAmount(parseFloat(e.target.value))}
          placeholder={`Máximo: ${maxAmount} RUB`}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: <InputAdornment position="start">₽</InputAdornment>
          }}
        />

        <TextField
          fullWidth
          type="date"
          label="Fecha de pago"
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.target.value)}
          sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          fullWidth
          label="Referencia bancaria"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          sx={{ mb: 2 }}
          placeholder="Número de transferencia, cheque, etc."
        />

        <TextField
          fullWidth
          label="Comentarios"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          multiline
          rows={3}
          sx={{ mb: 2 }}
          placeholder="Información adicional sobre este pago parcial..."
        />

        {/* Upload de comprobante */}
        <input
          type="file"
          id="proof-upload"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          accept=".jpg,.jpeg,.png,.gif,.pdf"
        />
        
        <Button
          fullWidth
          variant="outlined"
          startIcon={proofFile ? <ReceiptIcon /> : <CloudUploadIcon />}
          onClick={() => document.getElementById('proof-upload')?.click()}
          sx={{ mb: 2 }}
        >
          {proofFile ? proofFile.name : 'Subir comprobante (opcional)'}
        </Button>

        {amount > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Resumen:</strong>
            </Typography>
            <Typography variant="body2">
              Monto a pagar: {amount} RUB
            </Typography>
            <Typography variant="body2">
              Saldo restante: {maxAmount - amount} RUB
            </Typography>
            {maxAmount - amount === 0 && (
              <Chip 
                label="¡Este pago completa la mensualidad!" 
                color="success" 
                size="small" 
                sx={{ mt: 1 }}
              />
            )}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || amount <= 0 || amount > maxAmount}
        >
          {loading ? 'Procesando...' : 'Registrar Pago'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartialPaymentDialog;